<script setup>
/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { ref, computed, onMounted, provide } from 'vue';

/*==================================================================*/
/*================== IMPORTS DE COMPONENTES ========================*/
/*==================================================================*/

import TelaTabela from '@/components/v2/telas/TelaTabela/TelaTabela.vue';
import BotaoIconeBase from '@/components/v2/base/BotaoIconeBase/BotaoIconeBase.vue';
import EditarDadoModal from './EditarDado/EditarDadoModal.vue';
import VisualizarDadoModal from './VisualizarDado/VisualizarDadoModal.vue';

/*==================================================================*/
/*================== IMPORTS DE SERVICES ===========================*/
/*==================================================================*/

import OrdemDeServicoService from '@/api/v2/services/OrdemDeServicoService.js';

/*==================================================================*/
/*================== MODAL REFS ====================================*/
/*==================================================================*/

const modalEditarDadoRef = ref();
const modalVisualizarDadoRef = ref();

const disable = ref(false);

/*==================================================================*/
/*================== REFS DE DADOS E LOADED ========================*/
/*==================================================================*/

const ordensDeServico = ref([]);
const dados           = ref([]);
const jobs = ref([]);
const coordenadores = ref([]);
const loadedJobs = ref(false);
const loadedCoordenadores = ref(false);

/*==================================================================*/
/*================== FILTROS =======================================*/
/*==================================================================*/

const filtroCodigo          = ref('');
const filtroTipo            = ref('');
const filtroJob             = ref('');
const filtroCoordenador     = ref('');

/*==================================================================*/
/*================== HEADERS DA TABELA =============================*/
/*==================================================================*/

const dadosHeaders = [
    { text: 'Código',          value: 'codigo',                    type: 'string' },
    { text: 'Descrição',       value: 'descricao',                 type: 'string' },
    { text: 'Tipo',            value: 'osTipo',                    type: 'string' },
    { text: 'Job',             value: 'jobCodigo',                 type: 'string' },
    { text: 'Job Item',        value: 'jobItemSequencia',          type: 'string' },
    { text: 'Coordenador de Projeto', value: 'coordenadorDeProjetosNome', type: 'string' },
    { text: 'OS Mãe',          value: 'osMaeCodigo',               type: 'string' },
    { text: 'Qtd.',            value: 'osQuantidade',              type: 'number' },
    { text: 'Valor Disponível',value: 'valorDisponivel',           type: 'money'  },
    { text: 'Data Solicitação',value: 'dataSolicitacao',            type: 'date'   },
    { text: 'Prev. Encerramento', value: 'dataEncerramentoPrevista', type: 'date' },
    { text: 'Encerramento',    value: 'dataEncerramento',          type: 'date'   },
];

/*==================================================================*/
/*================== ALTERA CONTROLES DESATIVADOS ==================*/
/*==================================================================*/

const alteraDisable = (update = false) => {
    disable.value = update;
};

const normalizaCoordenador = (coordenador = {}) => {
    const id = coordenador?.id || coordenador?._id || '';
    const name = coordenador?.name || coordenador?.nome || coordenador?.descricao || coordenador?.description || '';

    return {
        ...coordenador,
        id,
        _id: coordenador?._id || id,
        name,
        nome: coordenador?.nome || name,
    };
};

/*==================================================================*/
/*================== VALIDAÇÕES DE FILTRO ==========================*/
/*==================================================================*/

const codigoFiltroValido = (codigo) => {
    if (filtroCodigo.value === '') return true;
    return codigo == filtroCodigo.value;
};

const tipoFiltroValido = (tipo) => {
    if (filtroTipo.value === '') return true;
    return tipo == filtroTipo.value;
};

const jobFiltroValido = (jobCodigo) => {
    if (filtroJob.value === '') return true;
    return jobCodigo == filtroJob.value;
};

const coordenadorFiltroValido = (coordenador) => {
    if (filtroCoordenador.value === '') return true;
    return coordenador == filtroCoordenador.value;
};

/*==================================================================*/
/*================== DADOS FILTRADOS ===============================*/
/*==================================================================*/

const dadosFiltrados = computed(() => {
    return dados?.value?.reduce((acc, dado) => {
        if (!codigoFiltroValido(dado?.codigo))                              return acc;
        if (!tipoFiltroValido(dado?.osTipo))                                return acc;
        if (!jobFiltroValido(dado?.jobCodigo))                              return acc;
        if (!coordenadorFiltroValido(dado?.coordenadorDeProjetosNome))     return acc;
        acc.push(dado);
        return acc;
    }, []);
});

/*==================================================================*/
/*================== ALTERA FILTROS ================================*/
/*==================================================================*/

const alteraFiltroCodigo      = (dado) => { filtroCodigo.value      = dado?.codigo                    || ''; };
const alteraFiltroTipo        = (dado) => { filtroTipo.value        = dado?.osTipo                    || ''; };
const alteraFiltroJob         = (dado) => { filtroJob.value         = dado?.jobCodigo                 || ''; };
const alteraFiltroCoordenador = (dado) => { filtroCoordenador.value = dado?.coordenadorDeProjetosNome || ''; };

/*==================================================================*/
/*================== FILTROS CONFIGURADOS ==========================*/
/*==================================================================*/

const filtros = computed(() => [
    { label: 'Código',      items: dados.value, itemText: 'codigo',                    onChange: alteraFiltroCodigo      },
    { label: 'Tipo',        items: dados.value, itemText: 'osTipo',                    onChange: alteraFiltroTipo        },
    { label: 'Job',         items: dados.value, itemText: 'jobCodigo',                 onChange: alteraFiltroJob         },
    { label: 'Coordenador', items: dados.value, itemText: 'coordenadorDeProjetosNome', onChange: alteraFiltroCoordenador },
]);

/*==================================================================*/
/*================== LOAD PRINCIPAL ================================*/
/*==================================================================*/

const loadOrdensDeServico = async () => {
    alteraDisable(true);
    const response = await OrdemDeServicoService.pegaViewOrdemDeServico();
    ordensDeServico.value = response?.data || [];
    alteraDisable(false);
};

const loadJobs = async () => {
    if (loadedJobs.value) return;
    const response = await OrdemDeServicoService.pegaViewJobs();
    jobs.value = Array.isArray(response?.data) ? response.data : [];
    loadedJobs.value = response?.status === 200;
};

const loadCoordenadores = async () => {
    if (loadedCoordenadores.value) return;
    const response = await OrdemDeServicoService.pegaViewCoordenadores();
    coordenadores.value = Array.isArray(response?.data)
        ? response.data.map((coordenador = {}) => normalizaCoordenador(coordenador))
        : [];
    loadedCoordenadores.value = response?.status === 200;
};

/*==================================================================*/
/*================== RELOAD ========================================*/
/*==================================================================*/

const reloadOrdensDeServico = async () => {
    await loadOrdensDeServico();
    preparaDados(ordensDeServico.value);
};

/*==================================================================*/
/*================== AO EDITAR / VISUALIZAR =========================*/
/*==================================================================*/

const aoVisualizarDado = async (dado) => {
    await modalVisualizarDadoRef.value?.abrirModal(dado);
};

const aoEditarDado = async (dado) => {
    await Promise.allSettled([loadJobs(), loadCoordenadores()]);
    // Garante que o campo 'itens' está presente e correto
    let dadoComItens = { ...dado };
    // Se não vier o campo 'itens', tenta buscar pelo backend
    if (!Array.isArray(dadoComItens.itens) || dadoComItens.itens.length === 0) {
        try {
            const resp = await OrdemDeServicoService.pegaViewOrdemDeServico();
            const osCompleta = Array.isArray(resp?.data)
                ? resp.data.find((os) => (os.id === dadoComItens.id || os._id === dadoComItens.id))
                : null;
            if (osCompleta && Array.isArray(osCompleta.itens)) {
                dadoComItens.itens = osCompleta.itens;
            }
        } catch (e) {
            // Se falhar, mantém como está
        }
    }
    await modalEditarDadoRef.value?.abrirModal(dadoComItens);
};

/*==================================================================*/
/*================== PREPARA DADOS =================================*/
/*==================================================================*/

const preparaDados = (dadosParaPreparar = []) => {
    dados.value = dadosParaPreparar.map((dado = {}) => ({
        id:                         dado.id                         ?? '',
        codigo:                     dado.codigo                     ?? '',
        descricao:                  dado.descricao                  ?? '',
        osTipo:                     dado.osTipo                     ?? '',
        job:                        dado.job                        ?? '',
        jobCodigo:                  dado.jobCodigo                  ?? '',
        jobItem:                    dado.jobItem                    ?? '',
        jobItemSequencia:           dado.jobItemSequencia           ?? '',
        itemOrdemDeServico:         dado.itemOrdemDeServico         ?? '',
        coordenadorDeProjetos:      dado.coordenadorDeProjetos      ?? '',
        coordenadorDeProjetosNome:  dado.coordenadorDeProjetosNome  ?? '',
        osMae:                      dado.osMae                      ?? '',
        osMaeCodigo:                dado.osMaeCodigo                ?? '',
        classificacaoContabil:      dado.classificacaoContabil      ?? '',
        osQuantidade:               dado.osQuantidade               ?? 0,
        valorDisponivel:            dado.valorDisponivel            ?? 0,
        dataSolicitacao:            dado.dataSolicitacao            ?? '',
        dataEncerramentoPrevista:   dado.dataEncerramentoPrevista   ?? '',
        dataEncerramento:           dado.dataEncerramento           ?? '',
        dataDetalhamentoPrevista:   dado.dataDetalhamentoPrevista   ?? '',
        dataDetalhamento:           dado.dataDetalhamento           ?? '',
        possuiDetalhamento:         dado.possuiDetalhamento         ?? false,
        dataMontagemPrevista:       dado.dataMontagemPrevista       ?? '',
        dataMontagem:               dado.dataMontagem               ?? '',
        possuiMontagem:             dado.possuiMontagem             ?? false,
        dataFabricacaoPrevista:     dado.dataFabricacaoPrevista     ?? '',
        dataFabricacao:             dado.dataFabricacao             ?? '',
        itens:                      dado.itens                      ?? [],
        owners:                     dado.owners                     ?? [],
    }));
};

/*==================================================================*/
/*================== PROVIDE =======================================*/
/*==================================================================*/

provide('alteraDisable', alteraDisable);

/*==================================================================*/
/*================== ON MOUNTED ====================================*/
/*==================================================================*/

onMounted(async () => {
    await loadOrdensDeServico();
    preparaDados(ordensDeServico.value);
});

</script>

<template>
    <div>

        <TelaTabela
            :disabled="disable"
            :filtros="filtros"
            :headers="dadosHeaders"
            :dados="dadosFiltrados"
        >

            <template #tela-titulo>
                Ordem de Serviço
            </template>

        <template #tabela-linha-botoes="{ dado, disabled }">

                <BotaoIconeBase
                    icon="mdi-eye"
                    color="#32434d"
                    text="Visualizar"
                    :disabled="disabled"
                    :click="() => aoVisualizarDado(dado)"
                ></BotaoIconeBase>

                <BotaoIconeBase
                    icon="mdi-pen"
                    color="#3E737B"
                    text="Editar"
                    :disabled="disabled"
                    :click="() => aoEditarDado(dado)"
                ></BotaoIconeBase>

            </template>

        </TelaTabela>

        <EditarDadoModal
            ref="modalEditarDadoRef"
            :disabled="disable"
            :jobs="jobs"
            :coordenadores="coordenadores"
            :ordensDeServico="dados"
            :onClose="reloadOrdensDeServico"
        ></EditarDadoModal>

        <VisualizarDadoModal
            ref="modalVisualizarDadoRef"
            :onClose="reloadOrdensDeServico"
        ></VisualizarDadoModal>

    </div>
</template>
